import DefaultPageLayout from "../../Layouts/DefaultPageLayout";
import React, {useEffect, useRef, useState} from "react";
import styles from './LoginPage.module.scss';
import {StyledText} from "../../Components/Text";
import Box from "Components/Box";
import InputLabel from "../../Components/InputLabel";
import {ControlledInput} from "../../Components/Input";
import Gap from "../../Components/Gap";
import Button from "../../Components/Button";
import {useTranslation} from "react-i18next";
import {useForm} from "react-hook-form";
import {
    GoogleSignInProvider,
    linkWithCredential, MicrosoftAuthProvider,
    signIn, signInWithPopup
} from "../../FirebaseSetup/auth";
import useToast from "Hooks/useToast";
import {Link, useNavigate} from "react-router-dom";
import OnboardingBoxContent from "../../Features/Onboarding/Components/OnboardingBoxContent";
import FlexRow from "Components/FlexRow";
import {trackClick} from "../../Utils/GAUtils";
import Separator from "../../Components/Separator";
import {GoogleAuthProvider, OAuthProvider} from "firebase/auth";

const LoginPage = () => {

    const navigate = useNavigate();
    const { showSuccessToast, showErrorToast } = useToast();

    const [loginTries, setLoginTries] = useState<number>(0);
    const [ linkingCredentials, setLinkingCredentials ] = useState<any>(null);

    const pageLoadTime = useRef(new Date().getTime());

    useEffect(() => {
        const controller = new AbortController();

        window.addEventListener('beforeunload', () => {
            if (loginTries > 0) {
                trackClick('login_dropoff', {loginTries})
            }
        }, { signal: controller.signal });

        return () => {
            controller.abort();
        }
    }, [loginTries]);

    const login = async (data: any) => {
        setLoading(true);

        const timeDiff = Math.round(((new Date().getTime()) - pageLoadTime.current) / 1000);

        trackClick('login_attempt', {timeToLogin: timeDiff, loginTries})

        try {
            const response = await signIn(data.email, data.password);
            
            if (!!linkingCredentials) {
                await linkWithCredential(response.user, linkingCredentials);

                showSuccessToast(t('credentialsLinkedMessage'))
            }
            
            trackClick('login_success', {loginTries, timeToLogin: timeDiff})
        } catch (e: any) {
            let message = t('somethingWentWrong');

            if (e.code === 'auth/user-not-found' || e.code === 'auth/wrong-password') {
                message = t('auth:invalidCredentials');
            }
            if (e.code === 'auth/invalid-email') {
                message = t('auth:invalidEmail');
            }

            trackClick('login_failed', {error: e.code, loginTries, timeToLogin: timeDiff})

            showErrorToast(message);
        } finally {
            setLoginTries(c => c + 1)
            setLoading(false);
        }
    }
    
    const loginWithProvider = async (provider: OAuthProvider | GoogleAuthProvider) => {
        const timeDiff = Math.round(((new Date().getTime()) - pageLoadTime.current) / 1000);

        trackClick(`login_${provider.providerId}_attempt`, {timeToLogin: timeDiff, loginTries})

        try {
            await signInWithPopup(provider);
        
            trackClick(`login_${provider.providerId}_success`, {timeToLogin: timeDiff, loginTries})
        } catch (e: any) {
            let message = t('somethingWentWrong');

            if (e.code === 'auth/account-exists-with-different-credential') {
                const credentials = OAuthProvider.credentialFromError(e);

                setLinkingCredentials(credentials);
            }
            
            trackClick(`login_${provider.providerId}_failed`, {timeToLogin: timeDiff, loginTries})

            if (e.code === 'auth/admin-restricted-operation') {
                message = t('youMustCreateAnAccountFirst')
            }

            showErrorToast(message);
        } finally {
            setLoginTries(c => c + 1)
        }   
    }

    const { t } = useTranslation();
    const [loading, setLoading] = useState<boolean>(false)

    const {control, handleSubmit} = useForm();

    return (
        <DefaultPageLayout contentMaxWidth={1440}>
            <div className={styles.container}>

                <div className={styles.headline}>
                    <StyledText type={"Headline"} weight={"Bold"} color={"secondary"}>
                        {t('signIn')}
                    </StyledText>
                </div>

                <Box style={{width: 840}}>
                    <form onSubmit={handleSubmit(login)} className={styles.content}>
                        <OnboardingBoxContent content={(
                            <>
                                <div>
                                    <button className={styles.ssoButton} onClick={() => loginWithProvider(GoogleSignInProvider)} type={"button"}>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path fillRule="evenodd" clipRule="evenodd" d="M23.52 12.2729C23.52 11.422 23.4436 10.6038 23.3018 9.81836H12V14.4602H18.4582C18.18 15.9602 17.3345 17.2311 16.0636 18.082V21.0929H19.9418C22.2109 19.0038 23.52 15.9274 23.52 12.2729Z" fill="#4285F4"/>
                                            <path fillRule="evenodd" clipRule="evenodd" d="M12.001 23.9998C15.241 23.9998 17.9573 22.9252 19.9428 21.0925L16.0646 18.0816C14.9901 18.8016 13.6155 19.2271 12.001 19.2271C8.87553 19.2271 6.23007 17.1161 5.28643 14.2798H1.27734V17.3889C3.25189 21.3107 7.31007 23.9998 12.001 23.9998Z" fill="#34A853"/>
                                            <path fillRule="evenodd" clipRule="evenodd" d="M5.28545 14.2799C5.04545 13.5599 4.90909 12.7908 4.90909 11.9999C4.90909 11.209 5.04545 10.4399 5.28545 9.71993V6.61084H1.27636C0.463636 8.23084 0 10.0636 0 11.9999C0 13.9363 0.463636 15.769 1.27636 17.389L5.28545 14.2799Z" fill="#FBBC05"/>
                                            <path fillRule="evenodd" clipRule="evenodd" d="M12.001 4.77273C13.7628 4.77273 15.3446 5.37818 16.5883 6.56727L20.0301 3.12545C17.9519 1.18909 15.2355 0 12.001 0C7.31007 0 3.25189 2.68909 1.27734 6.61091L5.28643 9.72C6.23007 6.88364 8.87553 4.77273 12.001 4.77273Z" fill="#EA4335"/>
                                        </svg>
                                        <StyledText type={"Caption"}>
                                            {t('signInWithGoogle')}
                                        </StyledText>
                                    </button>
                                </div>

                                <div>
                                    <button className={styles.ssoButton} onClick={() => loginWithProvider(MicrosoftAuthProvider)} type={"button"}>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M12 2H3V11H12V2Z" fill="#F25022"/>
                                            <path d="M12 12H3V21H12V12Z" fill="#00A4EF"/>
                                            <path d="M22 2H13V11H22V2Z" fill="#7FBA00"/>
                                            <path d="M22 12H13V21H22V12Z" fill="#FFB900"/>
                                        </svg>
                                        <StyledText type={"Caption"}>
                                            {t('signInWithMicrosoft')}
                                        </StyledText>
                                    </button>
                                </div>

                                <div style={{position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                    <div style={{position: 'absolute', backgroundColor: 'white', padding: '10px 30px'}}>
                                        <StyledText type={"Caption"}>{t('or')}</StyledText>
                                    </div>
                                    <Separator/>
                                </div>

                                {!!linkingCredentials && (
                                    <StyledText type={"Caption"} color={"error"} weight={"Bold"}>
                                        {t('linkingErrorMessage')}
                                    </StyledText>
                                )}
                                
                                <div>
                                    <InputLabel required>
                                        {t('email')}
                                    </InputLabel>
                                    <ControlledInput name={'email'} placeholder={t('yourEmail')} control={control} rules={{required: true}}/>
                                </div>

                                <div>
                                    <InputLabel required>
                                        {t('password')}
                                    </InputLabel>
                                    <ControlledInput name={'password'} placeholder={t('yourPassword')} type={"password"} control={control} rules={{required: true}}/>
                                </div>
                            </>
                        )} buttons={(
                            <Gap size={"medium"} className={"w-100"} direction={"vertical"}>
                                <FlexRow justify={"space-between"}>
                                    <Button text={t('auth:login.forgotPassword')} color={"primary"} outline type={"button"} onClick={() => navigate('/resetPassword')} />
                                    <Button text={t('signIn')} type={"submit"} state={loading ? "Loading" : "None"}/>
                                </FlexRow>
                                <FlexRow justify={"space-around"} className={styles.links}>
                                    <a style={{fontWeight: "bold", color: '#575757'}} href={`mailto:hello@the0mission.dk?subject=${t('auth:requestAccessSubject')}&body=${t('auth:requestAccessBody')}`}>
                                        <StyledText type={"Caption"} weight={"Bold"}>
                                            {t('auth:requestAccess')}
                                        </StyledText>
                                    </a>
                                    <div className={styles.borderRight} />
                                    <Link style={{fontWeight: "bold", color: '#575757'}} to={"/onboarding"}>
                                        <StyledText type={"Caption"} weight={"Bold"}>
                                            {t('auth:dontHaveAccount')}
                                        </StyledText>
                                    </Link>
                                </FlexRow>
                            </Gap>
                        )} />
                    </form>
                </Box>
            </div>
        </DefaultPageLayout>
    )
}

export default LoginPage
